import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
// import parse from "html-react-parser"; // Custom Code
import Carousel, { CarouselSlide, CarouselStyles } from "@store/Overrides/Components/Carousel";
import * as React from "react";

/* Custom Code */
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSelectedCategoryPath } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCatalogPageStateByPath } from "@insite/client-framework/Store/Data/CatalogPages/CatalogPagesSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import Clickable from "@insite/mobius/Clickable";
import useLanguage from "@store/Framework/Common/GoogleAnalytics/hooks/utilities/useLanguage";
import isBodyEmpty from "@store/Framework/Common/Utilities/emptyBody";
import LazyImage, { LazyImageProps } from "@store/MobiusOverrides/LazyImage/LazyImage";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

export const carouselStyles: CarouselStyles = {
    carouselControls: {
        css: css`
            .carousel-button-wrapper {
                display: none;
            }
        `,
    },
};

/* Custom Code */

interface ImageProps {
    image: {
        altText: string;
        id: string;
        link: LinkFieldValue;
        path: string;
    };
    imgProps?: LazyImageProps["imgProps"];
}

const enum fields {
    carouselChangeInterval = "carouselChangeInterval",
    showArrows = "showArrows",
    showDots = "showDots",
    slidesShown = "slidesShown",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.carouselChangeInterval]: string;
        [fields.showArrows]: boolean;
        [fields.showDots]: boolean;
        [fields.slidesShown]: number;
    };
}

export const otherStyles = {
    link: {
        // mergeCss: true,
        alignItems: "center",
        justifyContent: "center",
        // css: css`
        //     ${focusUpdateStyles(true)}
        // `,
    },
    image: {
        mergeCss: true,
        // css: css`
        //     z-index: -1;
        // `,
    },
};

const styles = carouselStyles;

const mapStateToProps = (state: ApplicationState) => {
    const location = getLocation(state);
    const categoryPath =
        getSelectedCategoryPath(state) ||
        (location.pathname.toLowerCase().startsWith("/content/") ? "" : location.pathname);
    const catalogPageState = getCatalogPageStateByPath(state, categoryPath);
    return {
        properties: catalogPageState.value?.properties,
    };
};

const mapDispatchToProps = {};

const Image: React.FC<ImageProps> = ({ image, imgProps }) => {
    const { url } = useGetLink(image.link);
    const img = <LazyImage {...otherStyles.image} imgProps={imgProps} src={image.path} altText={image.altText} />;

    return url ? (
        <Clickable style={{ justifyContent: "center" }} {...otherStyles.link} href={url}>
            {img}
        </Clickable>
    ) : (
        img
    );
};

const CategoryDetailCarousel = ({
    properties,
    fields: categoryFields,
    ...props
}: OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>) => {
    const lang = useLanguage();
    const propertyName = `carouselItemsData_${lang}`;
    if (!properties || !(propertyName in properties) || isBodyEmpty(properties[propertyName])) {
        return null;
    }

    const carouselStringItems = properties[propertyName] ?? "";
    const carouselItems = carouselStringItems.split("|");

    const images: { altText: string; id: string; link: LinkFieldValue; path: string }[] = carouselItems.map(
        (item, index) => {
            const [title, src, linkHref] = item.split(",");
            return {
                altText: title,
                id: `category-carousel-${index}`,
                link: {
                    type: "Url",
                    value: linkHref,
                },
                path: src,
            };
        },
    );

    const slides: CarouselSlide[] = images.map((img, index) => ({
        id: img.id,
        renderComponent: () => {
            return (
                <Image
                    key={img.id || index}
                    image={img}
                    imgProps={{
                        style: {
                            width: "auto",
                            maxWidth: "100%",
                        },
                    }}
                />
            );
        },
    }));

    const slidesPer = categoryFields[fields.slidesShown] ?? 1;
    const shouldAutoPlay = slides.length >= slidesPer ? true : undefined;
    const changeInterval = parseInt(categoryFields[fields.carouselChangeInterval], 10) ?? 3;
    const autoPlayDelaySeconds = changeInterval * 1000;
    const showDots = categoryFields[fields.showDots] ?? false;
    const showArrows = categoryFields[fields.showArrows] ?? false;

    return (
        <StyledWrapper data-test-selector="categoryDetailCarousel_htmlContent">
            <Carousel
                loop
                autoplay={shouldAutoPlay}
                autoplayDelay={autoPlayDelaySeconds}
                autoplayStopOnInteraction={true}
                {...carouselStyles}
                invertColor={true}
                id="categoryDetailCarousel"
                itemsAlign="center"
                itemsJustify="center"
                itemsPerSlide={slidesPer}
                keepArrows={showArrows}
                keepInvisibleArrows={false}
                showArrows={showArrows}
                showDots={showDots}
                slides={slides}
                overridedStyles={carouselStyles}
            />
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CategoryDetailCarousel),
    definition: {
        group: "Category Details",
        icon: "image-landscape",
        allowedContexts: ["CategoryDetailsPage", "ProductListPage"],
        fieldDefinitions: [
            {
                name: fields.slidesShown,
                displayName: "Slides Shown",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "1",
            },
            {
                name: fields.showArrows,
                displayName: "Show Arrows",
                editorTemplate: "CheckboxField",
                fieldType: "General",
                defaultValue: false,
            },
            {
                name: fields.showDots,
                displayName: "Show Dots",
                editorTemplate: "CheckboxField",
                fieldType: "General",
                defaultValue: false,
            },
            {
                name: fields.carouselChangeInterval,
                displayName: "Carousel Slide Interval",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "3",
            },
        ],
    },
};

export default widgetModule;
